'use client';
import { useState, useEffect, useContext } from 'react';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setTheme, setUser as updateUser } from '../../../../../apps/frontend/src/redux/slices/userSlice';
import toast from 'react-hot-toast';
import { ThemeContext } from '../../../../../apps/frontend/src/context/ThemeStore';

const Settings = ({ onBack }) => {
  const dispatch = useDispatch();
  const { isDarkTheme: contextDarkTheme, setIsDarkTheme } = useContext(ThemeContext);
  const [settings, setSettings] = useState({
    isDarkTheme: false,
    importantEmails: false,
    emails: false,
    importantNotifications: false,
    notifications: false,
    disablePredictedPercentile: false,
    disableSubmitButton: false,
    showFullScreenQuestionModal: false,
    enableMarkForReview: false,
    unlockSectionsBeforeTime: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        setLoading(true);
        const cachedUser = JSON.parse(localStorage.getItem('userProfile'));
        if (cachedUser?.settings) {
          setSettings((prev) => ({
            ...prev,
            ...cachedUser.settings,
            isDarkTheme: cachedUser.settings.isDarkTheme ?? false,
          }));
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchUserSettings();
  }, []);

  const handleThemeChange = async (newValue) => {
    try {
      setIsDarkTheme(newValue);
      setSettings((prev) => ({ ...prev, isDarkTheme: newValue }));

      const cachedUser = JSON.parse(localStorage.getItem('userProfile')) || {};
      const updatedUser = {
        ...cachedUser,
        settings: {
          ...cachedUser.settings,
          isDarkTheme: newValue,
        },
      };
      localStorage.setItem('userProfile', JSON.stringify(updatedUser));
      dispatch(updateUser(updatedUser));

      const root = window.document.documentElement;
      root.classList.remove('dark', 'light');
      root.classList.add(newValue ? 'dark' : 'light');
      sessionStorage.setItem('quizrr_dashboard_isDarkTheme', newValue.toString());

      if (cachedUser?._id) {
        await axios.patch('/api/user/profile/settings', {
          isDarkTheme: newValue,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update theme setting');
    }
  };

  const handleSettingChange = async (settingName, newValue) => {
    try {
      setSettings((prev) => ({ ...prev, [settingName]: newValue }));

      const cachedUser = JSON.parse(localStorage.getItem('userProfile')) || {};
      const updatedUser = {
        ...cachedUser,
        settings: {
          ...cachedUser.settings,
          [settingName]: newValue,
        },
      };
      localStorage.setItem('userProfile', JSON.stringify(updatedUser));
      dispatch(updateUser(updatedUser));

      const response = await axios.patch('/api/user/profile/settings', {
        [settingName]: newValue,
      });

      if (response.status === 200) {
        const updatedUserFromAPI = response.data.data;
        setSettings(updatedUserFromAPI.settings);
        localStorage.setItem('userProfile', JSON.stringify(updatedUserFromAPI));
        dispatch(updateUser(updatedUserFromAPI));
        toast.success('Setting updated successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update setting');
      setSettings((prev) => ({ ...prev, [settingName]: !newValue }));
    }
  };

  const ToggleSwitch = ({ id, checked, onChange }) => (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <div
        className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${
          checked ? 'bg-[var(--q3-accent-normal)]' : 'bg-[var(--q3-stroke-normal)]'
        }`}
      >
        <div
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-[var(--q3-surface-default)] rounded-full transition-transform duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </div>
    </label>
  );

  const SettingItem = ({ title, description, checked, onChange, id }) => (
    <div className="flex items-start justify-between py-4 last:border-b-0">
      <div className="flex-1 pr-4">
        <h3 className="text-[var(--q3-neutral-default)] font-medium text-sm mb-1">{title}</h3>
        <p className="text-[var(--q3-neutral-light)] text-xs leading-relaxed">{description}</p>
      </div>
      <ToggleSwitch id={id} checked={checked} onChange={(value) => onChange(id, value)} />
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--q3-surface-default)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--q3-accent-normal)]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--q3-surface-default)]">
      {/* Mobile Header */}
      {onBack && (
        <div className="sticky top-0 z-50 bg-[var(--q3-surface-default)] border-b border-[var(--q3-stroke-normal)] md:hidden">
          <div className="flex items-center gap-3 p-4">
            <button onClick={onBack} className="p-1">
              <ArrowLeftIcon size={20} className="text-[var(--q3-neutral-default)]" />
            </button>
            <div className="flex flex-col gap-1">
              <h1 className="text-lg font-semibold text-[var(--q3-neutral-default)]">Settings</h1>
              <p className="text-xs text-[var(--q3-neutral-light)]">Configure notifications and test preferences</p>
            </div>
          </div>
        </div>
      )}

      <div className="w-full mx-auto px-4 py-6 md:px-6 md:py-10">
        {/* Desktop Header */}
        <div className="hidden md:block mb-8 -mt-8">
          <h1 className="text-2xl font-bold text-[var(--q3-neutral-default)]">Settings</h1>
          <p className="text-sm text-[var(--q3-neutral-light)]">Configure notifications and test preferences</p>
        </div>

        {/* Display Section */}
        <div className="mb-3">
          <h2 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">Display</h2>
        </div>
        <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-xl p-5 mb-10">
          <div className="space-y-1">
            <div className="flex items-start justify-between py-4 last:border-b-0">
              <div className="flex-1 pr-4">
                <h3 className="text-[var(--q3-neutral-default)] font-medium text-sm mb-1">Dark Theme</h3>
                <p className="text-[var(--q3-neutral-light)] text-xs leading-relaxed">
                  Switch between light and dark mode
                </p>
              </div>
              <ToggleSwitch id="isDarkTheme" checked={settings.isDarkTheme} onChange={handleThemeChange} />
            </div>
          </div>
        </div>

        {/* Email & Notification Section */}
        <div className="mb-3">
          <h2 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">Email & Notification</h2>
        </div>
        <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-xl p-5 mb-10">
          <div className="space-y-1">
            <SettingItem
              id="importantEmails"
              title="Scorecard Emails"
              description="Receive email of the scorecard when you submit a test"
              checked={settings.importantEmails}
              onChange={handleSettingChange}
            />
            <SettingItem
              id="emails"
              title="Generic Emails"
              description="Receive emails of new test series, discounts etc."
              checked={settings.emails}
              onChange={handleSettingChange}
            />
            <SettingItem
              id="importantNotifications"
              title="Important Push Notifications"
              description="Receive notifications related to your purchased packs like upcoming test, result declaration etc."
              checked={settings.importantNotifications}
              onChange={handleSettingChange}
            />
            <SettingItem
              id="notifications"
              title="Generic Push Notifications"
              description="Receive notifications of new test series, discounts etc."
              checked={settings.notifications}
              onChange={handleSettingChange}
            />
          </div>
        </div>

        {/* Test & Analysis Settings Section */}
        <div className="mb-3">
          <h2 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">Test & Analysis Settings</h2>
        </div>
        <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-xl p-5">
          <div className="space-y-1">
            <SettingItem
              id="disablePredictedPercentile"
              title="Disable Predicted Percentile/Rank"
              description="On enabling this option, we won't show you your predicted percentile or rank wherever applicable"
              checked={settings.disablePredictedPercentile}
              onChange={handleSettingChange}
            />
            <SettingItem
              id="disableSubmitButton"
              title="Disable Submit Button"
              description="On enabling this option, we will disable the submit button on the test page & the test is auto-submitted when time is over"
              checked={settings.disableSubmitButton}
              onChange={handleSettingChange}
            />
            <SettingItem
              id="showQuestionLevelTimer"
              title="Show per Question Timer"
              description="On enabling this, we'll show time spent per question and nudge you if you're spending too long"
              checked={settings.showQuestionLevelTimer}
              onChange={handleSettingChange}
            />
            <SettingItem
              id="enableMarkForReview"
              title="Show 'Mark for Review' in BITSAT mocks"
              description={`As per the latest update, BITSAT 2026 may include a "Mark for Review" option and reviewed questions may be treated as answered. If you don't want this option in mocks, you can disable it here.`}
              checked={settings.enableMarkForReview}
              onChange={handleSettingChange}
            />
            <SettingItem
              id="unlockSectionsBeforeTime"
              title="Unlock Sections before Time (MHT CET)"
              description="On enabling this option, you will be able to unlock sections before the section time is over in MHT-CET tests"
              checked={settings.unlockSectionsBeforeTime}
              onChange={handleSettingChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
